import React from 'react';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const StatusToggleModal = ({
  isOpen,
  onClose,
  onConfirm,
  userName,
  currentStatus,
  newStatus
}) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose?.();
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  const isActivating = newStatus === 'active';
  const statusColor = isActivating ? 'green' : 'orange';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className={cn(
              'flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center',
              isActivating ? 'bg-green-100' : 'bg-orange-100'
            )}>
              {isActivating ? (
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                </svg>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">
                {isActivating ? 'Ativar usuário' : 'Desativar usuário'}
              </h3>
              <p className="text-sm text-muted-foreground">
                Alterar status da conta do usuário
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
            className="text-muted-foreground hover:text-foreground"
          />
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <p className="text-foreground">
              Tem certeza que deseja {isActivating ? 'ativar' : 'desativar'} o usuário{' '}
              <span className={cn(
                'font-semibold',
                isActivating ? 'text-green-600' : 'text-orange-600'
              )}>
                "{userName}"
              </span>?
            </p>
          </div>

          <div className={cn(
            'border rounded-lg p-3 mb-4',
            isActivating 
              ? 'bg-green-50 border-green-200' :'bg-orange-50 border-orange-200'
          )}>
            <div className="flex items-start space-x-2">
              {isActivating ? (
                <svg className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
              <div className={cn(
                'text-sm',
                isActivating ? 'text-green-800' : 'text-orange-800'
              )}>
                <p className="font-medium mb-1">
                  {isActivating ? 'Ativar este usuário irá:' : 'Desativar este usuário irá:'}
                </p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  {isActivating ? (
                    <>
                      <li>Restaurar o acesso ao sistema</li>
                      <li>Habilitar o login</li>
                      <li>Reativar as permissões do usuário</li>
                    </>
                  ) : (
                    <>
                      <li>Revogar o acesso ao sistema</li>
                      <li>Encerrar todas as sessões ativas</li>
                      <li>Desabilitar o login</li>
                    </>
                  )}
                </ul>
              </div>
            </div>
          </div>

          {!isActivating && (
            <p className="text-xs text-muted-foreground">
              Nota: os dados do usuário serão preservados e poderão ser reativados depois.
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancelar
          </Button>
          <Button
            variant={isActivating ? 'default' : 'warning'}
            onClick={handleConfirm}
            iconName={isActivating ? 'UserCheck' : 'UserX'}
            iconPosition="left"
            className={cn(
              isActivating 
                ? 'bg-green-600 hover:bg-green-700 text-white' :'bg-orange-600 hover:bg-orange-700 text-white'
            )}
          >
            {isActivating ? 'Ativar usuário' : 'Desativar usuário'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default StatusToggleModal;